import { Injectable } from '@angular/core';
import { jkRestReturn } from './jkInterface';

export class JkRestReturnImpl implements jkRestReturn {
  result = false;
  message = '';
  data = '';
  obj: any = null;
  request: any = null;
  rowsAffected = 0;
  log = '';
  errorLog = '';
  errorCode = 0;
  errorMessage = '';
  parent: any = null;

  clear(bool?: boolean): void {
    this.result = bool ?? false;
    this.message = '';
    this.data = '';
    this.obj = null;
    this.request = null;
    this.rowsAffected = 0;
    this.log = '';
    this.errorLog = '';
    this.errorCode = 0;
    this.errorMessage = '';
    this.parent = null;
  }

  addResult(xLog: string): void {
    this.log += xLog + '\n';
  }

  getResult(): string {
    return this.log;
  }

  addResultError(xLog: string, isLogger?: boolean): string {
    this.errorLog += xLog + '\n';
    if (isLogger) {
      console.error(xLog);
    }
    return this.errorLog;
  }

  getResultError(): string {
    return this.errorLog;
  }

  addResultWarning(xLog: string): string {
    // Implement warning logic if needed
    return '';
  }

  mergeLog(from: jkRestReturn): void {
    this.log += from.log;
    this.errorLog += from.errorLog;
  }

  ok(): void {
    this.result = true;
  }

  assign(r: jkRestReturn): void {
    Object.assign(this, r);
  }
}

/**
 * 驗證結果介面
 * 用於表示實體驗證的結果
 */
export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

/**
 * 驗證錯誤介面
 * 用於表示單一驗證錯誤
 */
export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

/**
 * EntityErrorHandler
 *
 * 業務邏輯錯誤處理類別，負責：
 * 1. 處理實體操作過程中的錯誤
 * 2. 標準化錯誤回應格式
 * 3. 處理驗證錯誤
 */
@Injectable({
  providedIn: 'root'
})
export class EntityErrorHandler {

  /**
   * 處理實體操作錯誤
   * @param error 錯誤物件
   * @param operation 操作名稱
   * @returns 標準化的錯誤回應
   */
  static handleEntityError(error: any, operation: string): jkRestReturn {
    console.error(`Entity operation error (${operation}):`, error);

    // 建立標準化錯誤回應
    const errorResponse = new JkRestReturnImpl();
    errorResponse.result = false;
    errorResponse.message = `${operation} 操作失敗`;

    // 處理不同類型的錯誤
    if (error instanceof Error) {
      errorResponse.message = `${operation} 操作失敗: ${error.message}`;
      errorResponse.obj = { errorType: 'Error', stack: error.stack };
    } else if (typeof error === 'string') {
      errorResponse.message = `${operation} 操作失敗: ${error}`;
    } else if (error && typeof error === 'object') {
      if (error.message) {
        errorResponse.message = `${operation} 操作失敗: ${error.message}`;
      }
      errorResponse.obj = error;
    }

    return errorResponse;
  }

  /**
   * 處理驗證錯誤
   * @param validationResult 驗證結果
   * @param entityName 實體名稱
   * @returns 標準化的錯誤回應
   */
  static handleValidationError(validationResult: ValidationResult, entityName: string): jkRestReturn {
    const response = new JkRestReturnImpl();
    if (validationResult.isValid) {
      response.result = true;
      response.message = `${entityName} 驗證通過`;
      return response;
    }

    // 取得第一個錯誤訊息作為主要訊息
    const firstError = validationResult.errors[0];
    const message = `${entityName} 驗證失敗: ${firstError.message}`;

    // 記錄驗證錯誤
    console.warn(`Validation error for ${entityName}:`, validationResult.errors);

    response.result = false;
    response.message = message;
    response.obj = {
      validationErrors: validationResult.errors,
      errorType: 'ValidationError'
    };
    return response;
  }

  /**
   * 建立驗證結果
   * @param isValid 是否有效
   * @param errors 錯誤列表
   * @returns 驗證結果物件
   */
  static createValidationResult(isValid: boolean, errors: ValidationError[] = []): ValidationResult {
    return { isValid, errors };
  }

  /**
   * 建立驗證錯誤
   * @param field 欄位名稱
   * @param message 錯誤訊息
   * @param code 錯誤代碼
   * @returns 驗證錯誤物件
   */
  static createValidationError(field: string, message: string, code: string = 'INVALID'): ValidationError {
    return { field, message, code };
  }

  /**
   * 驗證必填欄位
   * @param value 欄位值
   * @param fieldName 欄位名稱
   * @param displayName 顯示名稱
   * @returns 驗證錯誤或 null
   */
  static validateRequired(value: any, fieldName: string, displayName: string): ValidationError | null {
    if (value === undefined || value === null || value === '') {
      return this.createValidationError(fieldName, `${displayName} 為必填欄位`, 'REQUIRED');
    }
    return null;
  }

  /**
   * 驗證字串長度
   * @param value 欄位值
   * @param fieldName 欄位名稱
   * @param displayName 顯示名稱
   * @param minLength 最小長度
   * @param maxLength 最大長度
   * @returns 驗證錯誤或 null
   */
  static validateLength(
    value: string,
    fieldName: string,
    displayName: string,
    minLength: number,
    maxLength: number
  ): ValidationError | null {
    if (!value) return null;

    if (value.length < minLength) {
      return this.createValidationError(
        fieldName,
        `${displayName} 長度不得小於 ${minLength} 個字元`,
        'MIN_LENGTH'
      );
    }

    if (value.length > maxLength) {
      return this.createValidationError(
        fieldName,
        `${displayName} 長度不得超過 ${maxLength} 個字元`,
        'MAX_LENGTH'
      );
    }

    return null;
  }

  /**
   * 驗證數值範圍
   * @param value 欄位值
   * @param fieldName 欄位名稱
   * @param displayName 顯示名稱
   * @param min 最小值
   * @param max 最大值
   * @returns 驗證錯誤或 null
   */
  static validateRange(
    value: number,
    fieldName: string,
    displayName: string,
    min: number,
    max: number
  ): ValidationError | null {
    if (value === undefined || value === null) return null;

    if (value < min) {
      return this.createValidationError(
        fieldName,
        `${displayName} 不得小於 ${min}`,
        'MIN_VALUE'
      );
    }

    if (value > max) {
      return this.createValidationError(
        fieldName,
        `${displayName} 不得大於 ${max}`,
        'MAX_VALUE'
      );
    }

    return null;
  }

  /**
   * 驗證電子郵件格式
   * @param value 欄位值
   * @param fieldName 欄位名稱
   * @param displayName 顯示名稱
   * @returns 驗證錯誤或 null
   */
  static validateEmail(value: string, fieldName: string, displayName: string): ValidationError | null {
    if (!value) return null;

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(value)) {
      return this.createValidationError(
        fieldName,
        `${displayName} 格式不正確`,
        'INVALID_EMAIL'
      );
    }

    return null;
  }

  /**
   * 驗證日期格式
   * @param value 欄位值
   * @param fieldName 欄位名稱
   * @param displayName 顯示名稱
   * @returns 驗證錯誤或 null
   */
  static validateDate(value: string, fieldName: string, displayName: string): ValidationError | null {
    if (!value) return null;

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return this.createValidationError(
        fieldName,
        `${displayName} 日期格式不正確`,
        'INVALID_DATE'
      );
    }

    return null;
  }

  /**
   * 驗證多個規則
   * @param validations 驗證結果陣列
   * @returns 驗證結果物件
   */
  static validateAll(validations: (ValidationError | null)[]): ValidationResult {
    // 過濾掉 null 值，只保留實際的錯誤
    const errors = validations.filter(error => error !== null) as ValidationError[];

    return this.createValidationResult(errors.length === 0, errors);
  }
}
